import { audioContext, decodeAudio } from './context'

/**
 * Background music loop.
 *
 * Web Audio rather than <audio loop>: an HTMLAudioElement restarts the
 * file on every pass and the browser is allowed to insert a gap there, so
 * a lounge groove would tick once per lap. An AudioBufferSourceNode with
 * `loop = true` splices the buffer sample-accurately instead.
 *
 * `loopStart` skips the encoder's leading silence. MP3 is a padded format:
 * the decoder hands back a few milliseconds that were never in the source,
 * and looping over them is exactly the click the buffer was meant to
 * avoid. The trim is small enough to be inaudible on a sustained pad.
 *
 * The context is shared with the effects (`context.ts`), including its
 * autoplay unlocking — so muting the music never silences a win sound and
 * the first gesture brings both in.
 */

/** Encoder padding skipped at the loop point, in seconds. */
const LOOP_TRIM_S = 0.02

/** Length of the self-crossfade baked into the loop, in seconds. */
const SPLICE_S = 1.5

/**
 * Rebuild a buffer so that it loops seamlessly BY CONSTRUCTION.
 *
 * Generated music rarely ends on the exact waveform it began on, and a
 * loop point between two mismatched bars is an audible hiccup every lap —
 * exactly the "it keeps interrupting" fatigue. Instead of trusting the
 * generator, the head of the track is equal-power-crossfaded over its
 * tail: the result starts SPLICE_S seconds in and its final seconds are
 * already a blend into its own opening, so the loop point lands on
 * identical material by definition.
 *
 * Equal-power (sin/cos), not linear: two unrelated waveforms summed with
 * linear ramps dip by ~3 dB in the middle of the fade, which reads as the
 * music ducking once per lap.
 */
function makeSeamless(ctx: AudioContext, buffer: AudioBuffer): AudioBuffer {
  const rate = buffer.sampleRate
  const fade = Math.min(Math.floor(SPLICE_S * rate), Math.floor(buffer.length / 4))
  if (fade < rate / 10) return buffer
  const outLength = buffer.length - fade
  const out = ctx.createBuffer(buffer.numberOfChannels, outLength, rate)
  for (let ch = 0; ch < buffer.numberOfChannels; ch++) {
    const src = buffer.getChannelData(ch)
    const dst = out.getChannelData(ch)
    // Body: everything from `fade` onward, minus the tail being blended.
    for (let i = 0; i < outLength; i++) dst[i] = src[i + fade]
    // Tail: blend the original head in as the original tail fades out.
    for (let i = 0; i < fade; i++) {
      const t = i / fade
      const a = Math.cos((t * Math.PI) / 2)
      const b = Math.sin((t * Math.PI) / 2)
      dst[outLength - fade + i] = src[buffer.length - fade + i] * a + src[i] * b
    }
  }
  return out
}

/** Fade applied on mute/unmute, in seconds. A hard cut reads as a glitch. */
const FADE_S = 0.35

export interface MusicHandle {
  /** Stop playback and release the audio graph. */
  dispose: () => void
  /** Fade to playing (`true`) or silent (`false`). */
  setEnabled: (on: boolean) => void
  /**
   * Crossfade between the base loop and the free-spins loop. A no-op when
   * the theme ships no second track — callers switch modes blindly.
   */
  setMode: (mode: MusicMode) => void
}

export type MusicMode = 'base' | 'free'

/**
 * Crossfades between the two moods, in seconds. Asymmetric on purpose:
 * the bonus ARRIVES (a quick lift under the fanfare reads as an event),
 * but it EBBS — flowing back into the calm loop slowly enough that the
 * player feels the scene settle rather than hears a track change.
 */
const MODE_FADE_UP_S = 1.2
const MODE_FADE_DOWN_S = 3.0

/**
 * The free-spins layer plays louder than the base layer. A bonus that is
 * merely different but equally quiet does not feel like a bonus; the boost
 * stays modest because musicVolume is capped at 0.8 and the win sounds
 * must remain on top.
 */
const FREE_BOOST = 1.45

/**
 * Start the background music: a base loop, and optionally a second,
 * livelier loop for free spins. Silent, not fatal, if anything fails.
 *
 * Both loops RUN THE WHOLE TIME and the mode only moves their gains. The
 * alternative — starting the free track on demand — would enter it from
 * bar one on every trigger, and a crossfade into a track that is still at
 * its intro reads as a restart, not as the same scene heating up.
 *
 * The master/mode gains are separate stages on purpose: mute and mode can
 * then fade independently without recomputing each other's targets.
 */
export function playMusicLoop(
  url: string,
  volume: number,
  enabled: boolean,
  freeUrl: string | null = null,
): MusicHandle {
  const ctx = audioContext()
  if (!ctx) return { dispose: () => {}, setEnabled: () => {}, setMode: () => {} }

  const master = ctx.createGain()
  master.gain.value = enabled ? volume : 0
  master.connect(ctx.destination)

  const layers: Array<{ gain: GainNode; source: AudioBufferSourceNode | null }> = []
  let disposed = false
  let on = enabled
  let mode: MusicMode = 'base'

  const addLayer = (trackUrl: string, initialGain: number): void => {
    const gain = ctx.createGain()
    gain.gain.value = initialGain
    gain.connect(master)
    const layer: { gain: GainNode; source: AudioBufferSourceNode | null } = { gain, source: null }
    layers.push(layer)
    void (async () => {
      const decoded = await decodeAudio(trackUrl)
      if (!decoded || disposed) return
      const buffer = makeSeamless(ctx, decoded)
      const source = ctx.createBufferSource()
      source.buffer = buffer
      source.loop = true
      source.loopStart = Math.min(LOOP_TRIM_S, buffer.duration / 2)
      source.loopEnd = buffer.duration
      source.connect(gain)
      source.start(0, source.loopStart)
      layer.source = source
    })()
  }

  addLayer(url, 1)
  if (freeUrl) addLayer(freeUrl, 0)

  return {
    setEnabled(next: boolean) {
      if (disposed || next === on) return
      on = next
      // setTargetAtTime, not a linear ramp: it starts moving immediately
      // from wherever the gain currently is, so toggling mid-fade does not
      // jump.
      master.gain.cancelScheduledValues(ctx.currentTime)
      master.gain.setTargetAtTime(next ? volume : 0, ctx.currentTime, FADE_S / 3)
    },
    setMode(next: MusicMode) {
      if (disposed || next === mode || layers.length < 2) return
      mode = next
      const t = ctx.currentTime
      const fade = (next === 'free' ? MODE_FADE_UP_S : MODE_FADE_DOWN_S) / 3
      for (const [i, layer] of layers.entries()) {
        const on = (i === 0) === (next === 'base')
        const target = on ? (i === 1 ? FREE_BOOST : 1) : 0
        layer.gain.gain.cancelScheduledValues(t)
        layer.gain.gain.setTargetAtTime(target, t, fade)
      }
    },
    dispose() {
      if (disposed) return
      disposed = true
      for (const layer of layers) {
        try {
          layer.source?.stop()
        } catch {
          // Never started (still decoding) or already stopped.
        }
        layer.source?.disconnect()
        layer.gain.disconnect()
      }
      master.disconnect()
    },
  }
}
